import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { getUserAuthorizationHeader } from "../api/authHeaders";

type AuthedFetchBaseQueryOptions = {
  baseUrl: string;
  credentials?: RequestCredentials;
};

const setHeaderIfMissing = (headers: Headers, key: string, value: string) => {
  if (!headers.has(key)) headers.set(key, value);
};

const toHeaders = (input: FetchArgs["headers"]): Headers => {
  const headers = new Headers();
  if (!input) return headers;

  if (input instanceof Headers) {
    input.forEach((value, key) => headers.set(key, value));
    return headers;
  }

  if (Array.isArray(input)) {
    for (const pair of input) {
      if (Array.isArray(pair) && pair.length >= 2) {
        const [key, value] = pair;
        if (typeof key === "string" && typeof value === "string") {
          headers.set(key, value);
        }
      }
    }
    return headers;
  }

  for (const [key, value] of Object.entries(input)) {
    if (typeof value === "string") headers.set(key, value);
  }
  return headers;
};

export function authedFetchBaseQuery({
  baseUrl,
  credentials,
}: AuthedFetchBaseQueryOptions): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> {
  const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    credentials,
  });

  return async (args, api, extraOptions) => {
    const authHeader = await getUserAuthorizationHeader();

    const normalizedArgs: FetchArgs =
      typeof args === "string" ? { url: args } : { ...args };

    const headers = toHeaders(normalizedArgs.headers);
    setHeaderIfMissing(headers, "Content-Type", "application/json");
    if (authHeader) headers.set("Authorization", authHeader);

    return rawBaseQuery(
      {
        ...normalizedArgs,
        headers,
      },
      api,
      extraOptions,
    );
  };
}

